import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DropdownColor } from '@beeinventor/dasiot-react-component-lib';
import MapboxLanguage from '@mapbox/mapbox-gl-language';
import { styled, Typography } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import * as turf from '@turf/turf';
import maplibregl from 'maplibre-gl';

import {
  Area,
  AreaMultiPolygon,
  DropDownColorItem,
  EnvironmentPurpose,
  SettingMap,
} from '../../../types';

import { getAreasOfProject } from '../../../apis/AreaApi';
import { useAppSelector } from '../../../hooks';
import { useLocalStorageObject } from '../../../hooks/useLocalStorageObject';
import useMapboxArea from '../../../pages/dashboard/hooks/useMapboxArea';

import i18n from '../../../i18n';
import { addAreasLayer } from '../../../pages/dashboard/Map2d/addLayers';
import { getAreaColor } from '../../../utils/getAreaColor';
import { getMapStyle } from '../../../utils/MapboxUtils';
import EmptyData from '../../EmptyData';

import 'maplibre-gl/dist/maplibre-gl.css';

const Container = styled('div')`
  height: calc(100% - 48px);
  width: 100%;
  display: flex;
  flex-direction: column;
`;

const ContainerContent = styled('div')`
  height: 100%;
  width: 100%;
  border-radius: 4px;
`;

const ContentTitle = styled(Typography)`
  color: white;
  margin-bottom: 8px;
`;

const Content = styled('div')`
  border-radius: 4px;
  width: 100%;
  margin-top: 10px;
  background: rgba(0, 0, 0, 0.2);
  padding: 10px;
  color: white;
`;

const FieldLabel = styled(Typography)(({ theme }) => ({
  ...theme.externalTypography.body3,
  color: theme.color.box_bbg,
  marginBottom: '4px',
}));

const ContainerField = styled('div')`
  margin-bottom: 12px;
`;

interface ContainerDropdownProps {
  isSelected?: boolean;
}

// prettier-ignore
const ContainerDropdown = styled('div', { shouldForwardProp: (prop) => prop !== 'isSelected' })<ContainerDropdownProps>`
  & > .Dropdown-root {
    color: ${({ isSelected, theme }) => isSelected ? '#fff' : theme.color.secondary.$60};
    font-weight: 400;
    font-size: 14px;
    padding: 0 8px;
    height: 40px;
    background: rgba(0, 0, 0, 0.2);
    display: flex;
    align-items: center;
    gap: 8px;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
    min-width: 0;
    & > .Dropdown-icon {
      width: 32px;
      height: 32px;
      flex: 0 0 32px;
    }
  }
  & > .Dropdown--disabled {
    background: rgba(0, 0, 0, 0.806);
    color: ${({ theme }) => theme.color.secondary.$40};
  }
  & div[role='tooltip'] {
    & > .MuiBox-root {
      color: ${({ theme }) => theme.color.box_bbg};
      font-weight: 200;
      font-size: 12px;
      background: ${({ theme }) => theme.color.secondary.$100};
      max-height: 120px;
      overflow-x: auto;
      & > .Dropdown-item {
        height: 40px;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        display: flex;
        align-items: center;
        min-width: 0;
      }
      .Dropdown-icon {
        flex: 0 0 40px;
      }
    }
    z-index: 5;
  }
`;

const ContainerMap = styled('div')`
  height: 140px;
  margin-top: 12px;
  border-radius: 4px;
  z-index: 1;
  overflow: hidden;
`;

interface PurposeAreaViewProps {
  projectId: string | undefined;
  selectedAreaId: string | undefined;
  selectedPurpose: EnvironmentPurpose | undefined;
}

const PurposeAreaView: React.FC<PurposeAreaViewProps> = ({
  projectId,
  selectedAreaId,
  selectedPurpose,
}) => {
  const { i18n: i18nInstance, t } = useTranslation();
  const project = useAppSelector((store) => store.projects.currentProject);
  const { projectArea } = useAppSelector((store) => store.projects);

  const [mapRef, setMapRef] = useState<HTMLDivElement | null>(null);
  const mapboxRef = useRef<maplibregl.Map | null>(null);
  const popupRefArea = useRef<maplibregl.Popup | null>(null);
  const [isMapReady, setIsMapReady] = useState(false);

  const [mapLocal] = useLocalStorageObject<SettingMap>(
    'mapStyleSetting_',
    projectId,
  );

  const { data: filteredAreas } = useQuery({
    queryKey: [
      'get-project-areas-without-boundary-path-pipe',
      projectId,
      i18nInstance.language,
    ],
    queryFn: async () => {
      const localAreas: Area[] = [];
      const load = async (nextCursor?: string) => {
        const res = await getAreasOfProject(projectId as string, {
          nextCursor,
          selectVisible: false,
        });
        res.data.data.forEach((area) => localAreas.push(area));
        if (res.data.paging.nextCursor) await load(res.data.paging.nextCursor);
      };
      await load();
      return localAreas
        .filter(
          (area) =>
            area.type !== 'boundary' &&
            area.type !== 'path' &&
            area.type !== 'pipe',
        )
        .sort((a, b) => {
          const typeResult = a.type.localeCompare(
            b.type,
            i18nInstance.language,
          );
          if (typeResult === 0) {
            return a.name.localeCompare(b.name, i18nInstance.language);
          }
          return typeResult;
        });
    },
    enabled: !!projectId,
    initialData: [],
  });

  const areaList = useMemo<DropDownColorItem[]>(() => {
    return filteredAreas.map((a) => ({
      id: a.id,
      value: a.id,
      name: a.name,
      color: getAreaColor(a.type),
    }));
  }, [filteredAreas]);

  const isAreaFound = useMemo(
    () => !!selectedAreaId && areaList.some((a) => a.id === selectedAreaId),
    [areaList, selectedAreaId],
  );

  const selectedArea = useMemo<Area | undefined>(() => {
    if (!selectedAreaId) return undefined;
    const found = projectArea?.find((area) => area.id === selectedAreaId);
    if (!found) return undefined;
    return { ...found, active: true, visible: true };
  }, [selectedAreaId, projectArea]);

  useEffect(() => {
    const initial = () => {
      const center: [number, number] = project?.center ?? [121.5654, 25.033];

      mapboxRef.current = new maplibregl.Map({
        container: 'purpose-area-view-map',
        zoom: 13,
        style: getMapStyle(mapLocal?.mapStyle),
        center,
        attributionControl: false,
      });

      mapboxRef.current.addControl(
        new MapboxLanguage({
          defaultLanguage: i18n.language,
        }) as unknown as maplibregl.IControl,
      );

      mapboxRef.current.on('load', () => {
        if (mapboxRef.current) {
          addAreasLayer(mapboxRef.current);

          popupRefArea.current = new maplibregl.Popup({
            closeButton: false,
            closeOnClick: false,
            className: 'tooltip-area',
          });
        }

        setIsMapReady(true);
      });
    };

    if (mapRef && !mapboxRef.current) {
      initial();
    }

    return () => {
      mapboxRef.current?.remove();
      mapboxRef.current = null;
      setIsMapReady(false);
    };
  }, [mapRef, mapLocal]);

  useEffect(() => {
    if (!isMapReady || !selectedArea) return;
    const geometry = selectedArea.area as AreaMultiPolygon;
    if (!geometry || geometry.length === 0) return;
    const feature = turf.multiPolygon(geometry);
    const center = turf.centerOfMass(feature);
    mapboxRef.current?.flyTo({
      center: center.geometry.coordinates as [number, number],
      zoom: 18,
      duration: 800,
    });
  }, [selectedAreaId, isMapReady]);

  useMapboxArea(
    isMapReady,
    selectedArea ? [selectedArea] : [],
    mapboxRef,
    popupRefArea,
  );

  return (
    <Container>
      <ContentTitle>
        {t('project-settingJson.navigator.purpose-area')}
      </ContentTitle>
      <ContainerContent>
        {selectedPurpose || selectedAreaId ? (
          <Content>
            <ContainerField>
              <FieldLabel>{t('purpose')}</FieldLabel>
              <Typography variant="body2">
                {selectedPurpose ? t(selectedPurpose) : '-'}
              </Typography>
            </ContainerField>

            <ContainerField>
              <FieldLabel>{t('area')}</FieldLabel>
              {isAreaFound ? (
                <ContainerDropdown isSelected={!!selectedAreaId}>
                  <DropdownColor
                    list={areaList}
                    onSelect={() => {}}
                    selectedId={selectedAreaId}
                    mode="dark"
                    popperProps={{
                      disablePortal: true,
                      placement: 'bottom-start',
                    }}
                    placeholder={t('area')}
                    disabled
                  />
                </ContainerDropdown>
              ) : (
                <Typography variant="body2" sx={{ color: 'white' }}>
                  -
                </Typography>
              )}
            </ContainerField>

            <ContainerMap
              ref={(ref) => setMapRef(ref)}
              id="purpose-area-view-map"
            />
          </Content>
        ) : (
          <EmptyData />
        )}
      </ContainerContent>
    </Container>
  );
};

export default PurposeAreaView;
